import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'
import { escapeHtml } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-exhibition-type-field', {
  title: "Type d'exposition*",
  placeholder: "Rechercher un type d'exposition",
  choose: 'Choisir',
})

Locale.getInstance().registerKeySet('en', 'lvc-exhibition-type-field', {
  title: 'ExhibitionTypes',
  placeholder: 'Search for a exhibitionType',
  choose: 'Choose',
})

export interface LvcExhibitionTypeField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export class LvcExhibitionTypeField extends MinzeElement {
  private exhibitionTypes: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = ['title', 'placeholder', ['default-value', ''], ['on-change', '']]

  reactive: Reactive = [
    ['value', ''],
    ['valueSelected', false],
  ]

  static observedAttributes = ['title', 'on-change']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  async onReady() {
    this.exhibitionTypes = await AdditionalTypes.getInstance().getAll()

    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedIds = JSON.parse(decodeURIComponent(this.defaultValue)).map((k: any) => k.id)
    }

    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onExhibitionTypeClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }

    this.selectedIds = [value]
    this.valueSelected = true

    this.serializeValue()
    this.onToggle()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let objects = this.exhibitionTypes.filter(k => this.selectedIds.indexOf(k.id) > -1)
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('change', objects)
  }

  getExhibitionTypesLabel() {
    if (this.selectedIds.length === 0) {
      return Locale.getKey('lvc-exhibition-type-field.choose')
    }

    let labels = this.exhibitionTypes
      .filter(k => this.selectedIds.indexOf(`${k.id}`) > -1)
      .map(k => k.label)

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderExhibitionType = (exhibitionType: any) => {
    if (
      this.filter &&
      exhibitionType.label.toLowerCase().indexOf(this.filter.toLowerCase()) === -1
    ) {
      return ''
    }

    let isSelected = this.selectedIds.length > 0 && this.selectedIds[0] === `${exhibitionType.id}`

    return `
          <button 
            name="exhibitionType"
            class="${isSelected ? 'selected' : 'not-selected'}" 
            data-id="${exhibitionType.id}">
            ${exhibitionType.label}
            ${!isSelected ? '' : '<lvc-circle-check-select-icon></lvc-circle-check-select-icon>'}
          </button>
        `
  }

  renderExhibitionTypes = () => {
    let html = ''

    for (let i = 0; i < this.exhibitionTypes.length; i++) {
      html += this.renderExhibitionType(this.exhibitionTypes[i])
    }

    return html
  }

  html = () => {
    return `
          <div class="title">${this.title || Locale.getKey('lvc-exhibition-type-field.title')}</div>
          <div class="input">
            <button class="dropbtn" name="toggle">
              ${this.getExhibitionTypesLabel()}
              <lvc-chevron-down-icon></lvc-chevron-down-icon>
            </button>
            <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
            <div class="dropdown-content ${this.open ? 'show' : ''}" style="background-color: ${this.open ? '#f9f9f9' : 'var(--Ciel, #BFF0FF)'}">
              <div class="header">
                <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-exhibition-type-field.placeholder')}">
                <lvc-chevron-up-icon></lvc-chevron-up-icon>
              </div>
              <div class="exhibitionTypes" data-selected="${this.selectedIds.length}">
                ${this.renderExhibitionTypes()}
              </div>
            </div> 
          </div>
        `
  }

  css = () => {
    return `
      :host {
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;

        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
        background: ${this.valueSelected ? `var(--Ciel, #BFF0FF);` : `transparent;`}
      }

      .exhibitionTypes {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .exhibitionTypes button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .exhibitionTypes button:hover {
        background-color: var(--Ciel, #BFF0FF);
        padding-left: 64px;
      }

      .exhibitionTypes button:last-child {
        border-bottom: none;
      }

      .exhibitionTypes button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 16px;
        font-size: 16px;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: 100%;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="exhibitionType"]', 'click', this.onExhibitionTypeClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
